import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { Canvas, useFrame } from '@react-three/fiber';
import { Points, PointMaterial } from '@react-three/drei';
import * as random from 'maath/random/dist/maath-random.esm';

// Type declarations for maath
declare module 'maath/random/dist/maath-random.esm' {
  export function inSphere(points: Float32Array, options: { radius: number }): Float32Array;
}

// Type for our Points reference
type PointsRef = React.ElementRef<typeof Points>;

const DNAHelix3D = () => {
  const ref = useRef<PointsRef>(null);
  
  // Generate DNA strand points
  const points = React.useMemo(() => {
    const points = [];
    for (let i = 0; i < 100; i++) {
      const angle = i * 0.2;
      const x = Math.sin(angle) * 2;
      const y = Math.cos(angle) * 2;
      const z = i * 0.1;
      points.push(x, y, z);
      
      // Complementary strand
      points.push(-x, -y, z);
    }
    return new Float32Array(points);
  }, []);

  useFrame((state, delta) => {
    if (ref.current) {
      ref.current.rotation.z += delta * 0.2;
      ref.current.position.y = Math.sin(state.clock.elapsedTime) * 0.5;
    }
  });

  return (
    <Points ref={ref} positions={points} stride={3}>
      <PointMaterial 
        transparent
        color="#00f0ff"
        size={0.15}
        sizeAttenuation={true}
        depthWrite={false}
      />
    </Points>
  );
};

interface NucleotideParticle {
  position: [number, number, number];
  color: string;
  size: number;
}

const FloatingNucleotides = () => {
  const particles = React.useMemo<NucleotideParticle[]>(() => {
    const sphere = random.inSphere(new Float32Array(500), { radius: 5 });
    const particles: NucleotideParticle[] = [];
    for (let i = 0; i < 500; i += 3) {
      particles.push({
        position: [sphere[i] * 10, (sphere[i] + 1) * 5, (sphere[i] - 1) * 5],
        color: i % 2 === 0 ? '#ff00aa' : '#00f0ff',
        size: Math.random() * 0.5 + 0.1
      });
    }
    return particles;
  }, []);

  return (
    <>
      {particles.map((particle, i) => (
        <mesh
          key={i}
          position={particle.position}
        >
          <sphereGeometry args={[0.1, 16, 16]} />
          <meshBasicMaterial color={particle.color} />
        </mesh>
      ))}
    </>
  );
};

const Hero = () => {
  return (
    <section className="relative h-screen overflow-hidden bg-gradient-to-br from-blue-900 to-purple-900">
      {/* 3D DNA Background */}
      <div className="absolute inset-0 opacity-30">
        <Canvas camera={{ position: [0, 0, 10], fov: 75 }}>
          <ambientLight intensity={0.5} />
          <DNAHelix3D />
          <FloatingNucleotides />
        </Canvas>
      </div>

      {/* Content */}
      <motion.div 
        className="relative z-10 h-full flex items-center justify-center text-center px-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div>
          <motion.h1
            className="text-5xl md:text-7xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-cyan-300 to-pink-400"
            initial={{ y: 50 }}
            animate={{ y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            Revolutionizing Cancer Treatment
          </motion.h1>
          
          <motion.p
  className="text-xl md:text-2xl max-w-2xl mx-auto mb-8 relative"
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ 
    delay: 0.6,
    duration: 1.2,
    ease: [0.16, 1, 0.3, 1]
  }}
>
  {/* Animated underline */}
  <motion.span 
    className="absolute -bottom-2 left-0 h-0.5 bg-gradient-to-r from-blue-400 to-indigo-500"
    initial={{ scaleX: 0, originX: 0 }}
    animate={{ scaleX: 1 }}
    transition={{
      delay: 1.8,
      duration: 0.8,
      ease: "circOut"
    }}
    style={{ width: '100%' }}
  />
  
  {/* Main text with improved contrast */}
  <span className="relative overflow-hidden inline-block mr-2">
    <motion.span
      className="inline-block text-gray-100"
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      transition={{ delay: 0.7, duration: 0.8 }}
    >
      Our
    </motion.span>
  </span>
  
  <span className="relative overflow-hidden inline-block">
    <motion.span
      className="font-semibold text-white bg-gradient-to-r from-blue-400 to-indigo-500 px-1 rounded-md inline-block"
      initial={{ y: "100%", opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ 
        delay: 0.9,
        duration: 0.8,
        ease: "backOut"
      }}
    >
      3'UTR Engineering
    </motion.span>
  </span>
  
  <span className="relative overflow-hidden inline-block ml-2">
    <motion.span
      className="inline-block text-gray-200"
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      transition={{ delay: 1.1, duration: 0.8 }}
    >
      Platform
    </motion.span>
  </span>
  
  <span className="relative overflow-hidden inline-block ml-2">
    <motion.span
      className="inline-block text-gray-100 font-medium"
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      transition={{ delay: 1.2, duration: 0.8 }}
    >
      Targets
    </motion.span>
  </span>
  
  <span className="relative overflow-hidden inline-block ml-2">
    <motion.span
      className="inline-block text-white font-medium"
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      transition={{ delay: 1.3, duration: 0.8 }}
    >
      Disease
    </motion.span>
  </span>
  
  <span className="relative overflow-hidden inline-block ml-2">
    <motion.span
      className="inline-block text-gray-200"
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      transition={{ delay: 1.4, duration: 0.8 }}
    >
      at
    </motion.span>
  </span>
  
  <span className="relative overflow-hidden inline-block ml-2">
    <motion.span
      className="inline-block text-gray-200"
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      transition={{ delay: 1.5, duration: 0.8 }}
    >
      the
    </motion.span>
  </span>
  
  <span className="relative overflow-hidden inline-block ml-2">
    <motion.span
      className="inline-block text-white font-medium"
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      transition={{ delay: 1.6, duration: 0.8 }}
    >
      Genetic
    </motion.span>
  </span>
  
  <span className="relative overflow-hidden inline-block ml-2">
    <motion.span
      className="inline-block text-white font-medium"
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      transition={{ delay: 1.7, duration: 0.8 }}
    >
      Level
    </motion.span>
  </span>
  <span className="relative overflow-hidden inline-block ml-2">
    <motion.span
      className="inline-block text-white font-medium"
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      transition={{ delay: 1.8, duration: 0.8 }}
    >
      with
    </motion.span>
  </span>
  <span className="relative overflow-hidden inline-block ml-2">
    <motion.span
      className="inline-block text-white font-medium"
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      transition={{ delay: 1.9, duration: 0.8 }}
    >
      Precision
    </motion.span>
  </span>
  <span className="relative overflow-hidden inline-block ml-2">
    <motion.span
      className="inline-block text-white font-medium"
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      transition={{ delay: 1.10, duration: 0.8 }}
    >
      and
    </motion.span>
  </span>
  <span className="relative overflow-hidden inline-block ml-2">
    <motion.span
      className="inline-block text-white font-medium"
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      transition={{ delay: 1.11, duration: 0.8 }}
    >
      efficacy
    </motion.span>
  </span>
</motion.p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-8 py-3 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold shadow-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.5 }}
          >
            Explore Our Technology
          </motion.button>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;